// Detects whether the Daf Confirmation dashboard is running EMBEDDED inside
// Daf One (as an iframe). Daf One appends `&embedded=1` to the iframe URL
// (see: daf inventory → dashboard/app/api/me/confirmation-link/route.ts).
//
// Why sessionStorage: this SPA (react-router) drops query params on internal
// navigation, so we capture `embedded=1` ONCE on the initial load and persist
// it for the lifetime of the tab. sessionStorage (not localStorage) is
// deliberate — it's scoped to this exact tab/iframe, so opening the standalone
// dashboard in a normal tab is never affected.
//
// Used to HIDE Daf Confirmation's own payment/billing UI while inside Daf One
// (Daf One manages the merchant's confirmation billing via the managed-billing
// bridge — the merchant tops up from Daf One's own billing, not here).
const KEY = 'daf_embedded';

export function setEmbeddedFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1') {
      sessionStorage.setItem(KEY, '1');
    }
  } catch (_) {
    /* no-op (SSR / storage disabled) */
  }
}

export function isDafOneEmbedded() {
  // (1) explicit signal — Daf One's ?embedded=1, captured on first load.
  try {
    if (sessionStorage.getItem(KEY) === '1') return true;
  } catch (_) {
    /* storage disabled — fall through to frame check */
  }
  // (2) fallback — rendered inside ANY iframe. This dashboard is only ever
  // embedded by Daf One, so being framed is itself a reliable "inside Daf One"
  // signal. This makes payment-hiding work even before the Daf One side ships
  // the ?embedded=1 param. A cross-origin frame (Daf One is a different
  // subdomain) makes `window.top` access throw → treat as embedded.
  try {
    return window.self !== window.top;
  } catch (_) {
    return true;
  }
}

// Daf One passes the merchant's REAL brand name (from Daf One's brands.name) as
// `&brandName=` on the iframe URL so the sidebar shows it instead of the
// confirmation client's own brand_name (often just the shop handle). Captured
// once on first load (same reason as embedded: react-router drops query params).
const NAME_KEY = 'daf_one_brand_name';

export function setBrandNameFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const bn = params.get('brandName');
    if (bn && bn.trim()) sessionStorage.setItem(NAME_KEY, bn.trim());
  } catch (_) {
    /* no-op (SSR / storage disabled) */
  }
}

export function getDafOneBrandName() {
  try {
    return sessionStorage.getItem(NAME_KEY) || null;
  } catch (_) {
    return null;
  }
}
